import { useEffect, useState } from "react";
import BRANDING, { getCurrentBranding } from "../../utils/branding";
import GoogleTranslate from "../common/GoogleTranslate";
import { teamService } from "../../services/partnerService";
import { useWhiteLabel } from "../../contexts/WhiteLabelContext";
import { Mail, Phone, MapPin } from "lucide-react";
import { Twitter as XIcon, Linkedin as LinkedInIcon } from "./SocialIcons";

const QUICK_LINKS = [
  { label: "Home", href: "/home" },
  { label: "ISO Certifications", href: "/iso-certification-pathways" },
  { label: "Courses", href: "/course-catalog" },
  { label: "About Us", href: "/about-us" },
  { label: "Contact Us", href: "/contact-us" },
];

const RESOURCES_LINKS = [
  { label: "Blogs", href: "/blogs" },
  { label: "Case Studies", href: "/case-studies" },
  { label: "FAQ", href: "/faq" },
  { label: "Standards Guide", href: "/iso-standards-guide" },
];

const LEGAL_LINKS = [
  { label: "Privacy Policy", href: "/privacy-policy" },
  { label: "Terms of Service", href: "/terms-of-service" },
  { label: "Cookie Policy", href: "/cookie-policy" },
];

const SECTION_HEADING =
  "text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-5";

const FooterLink = ({ href, label }) => (
  <li>
    <a
      href={href}
      className="text-[13px] text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
    >
      {label}
    </a>
  </li>
);

// --- Partner Footer ---
const PartnerFooter = ({ partner }) => {
  const displayName =
    partner?.branding_settings?.store_name || partner?.name || "Partner Store";
  const primaryColor = partner?.branding_settings?.primary_color || "#059669";

  const twitterHandle =
    partner?.branding_settings?.social_twitter ||
    partner?.branding_settings?.social?.twitter;
  const linkedinHandle =
    partner?.branding_settings?.social_linkedin ||
    partner?.branding_settings?.social?.linkedin;
  const description =
    partner?.branding_settings?.welcome_message ||
    partner?.branding_settings?.tagline;
  const contactPhone =
    partner?.branding_settings?.contact_phone ||
    partner?.branding_settings?.support_whatsapp;
  const locationLine = [
    partner?.branding_settings?.city,
    partner?.branding_settings?.country,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <footer className="bg-white dark:bg-gray-950 border-t border-gray-100 dark:border-gray-800/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-10 pb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10 mb-8 text-center md:text-left">
          <div>
            <div className="flex flex-col items-center md:items-start gap-2 mb-3">
              {partner?.branding_settings?.logo_url ? (
                <img
                  src={partner.branding_settings.logo_url}
                  alt={displayName}
                  className="h-10 w-auto object-contain"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <div
                  className="h-10 w-10 rounded-md flex items-center justify-center text-white text-[15px] font-bold"
                  style={{ backgroundColor: primaryColor }}
                >
                  {displayName.charAt(0)}
                </div>
              )}
              <span className="text-[15px] font-semibold text-gray-900 dark:text-white tracking-tight">
                {displayName}
              </span>
            </div>
            {description && (
              <p className="text-[13px] text-gray-500 leading-relaxed max-w-xs mx-auto md:mx-0">
                {description}
              </p>
            )}
            {(twitterHandle || linkedinHandle) && (
              <div className="flex gap-2 mt-4 justify-center md:justify-start">
                {twitterHandle && (
                  <a
                    href={/^https:\/\//.test(twitterHandle) ? twitterHandle : `https://twitter.com/${twitterHandle.replace("@", "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md flex items-center justify-center transition-colors"
                  >
                    <XIcon className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                  </a>
                )}
                {linkedinHandle && (
                  <a
                    href={/^https:\/\//.test(linkedinHandle) ? linkedinHandle : `https://linkedin.com/${linkedinHandle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md flex items-center justify-center transition-colors"
                  >
                    <LinkedInIcon className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                  </a>
                )}
              </div>
            )}
          </div>

          <div>
            <h4 className={SECTION_HEADING}>Contact</h4>
            <div className="space-y-3">
              {partner?.branding_settings?.support_email && (
                <a
                  href={`mailto:${partner.branding_settings.support_email}`}
                  className="flex items-center gap-2 justify-center md:justify-start text-[13px] text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors break-all"
                >
                  <Mail className="w-3.5 h-3.5 shrink-0" />
                  <span className="min-w-0">
                    {partner.branding_settings.support_email}
                  </span>
                </a>
              )}
              {contactPhone && (
                <a
                  href={`tel:${contactPhone}`}
                  className="flex items-center gap-2 justify-center md:justify-start text-[13px] text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <Phone className="w-3.5 h-3.5 shrink-0" />
                  <span>{contactPhone}</span>
                </a>
              )}
            </div>
          </div>

          {(partner?.branding_settings?.address || locationLine) && (
            <div>
              <h4 className={SECTION_HEADING}>Location</h4>
              <div className="space-y-1.5 text-[13px] text-gray-500 dark:text-gray-400">
                {partner?.branding_settings?.address && (
                  <div className="flex items-start gap-2 justify-center md:justify-start">
                    <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>{partner.branding_settings.address}</span>
                  </div>
                )}
                {locationLine && (
                  <p
                    className={
                      partner?.branding_settings?.address ? "md:ml-[22px]" : ""
                    }
                  >
                    {locationLine}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-gray-100 dark:border-gray-800/50 pt-5 text-center text-[12px] text-gray-400 dark:text-gray-600">
          © {new Date().getFullYear()} {displayName}. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

// --- Main Footer ---
const Footer = () => {
  const branding = BRANDING;

  const { isWhiteLabel, partnerSlug } = useWhiteLabel();
  const PARTNER_DOMAIN =
    import.meta.env.VITE_PARTNER_DOMAIN || "procertcompliance.com";
  const hostname = window.location.hostname.toLowerCase();
  const isProCertDev = import.meta.env.VITE_SHOW_PROCERT_HOME === "true";
  const isProCertDomain =
    hostname === PARTNER_DOMAIN ||
    hostname === `www.${PARTNER_DOMAIN}` ||
    (isProCertDev && hostname === "localhost");

  const [partner, setPartner] = useState(null);

  useEffect(() => {
    let active = true;
    setPartner(null);
    if (isWhiteLabel && partnerSlug) {
      teamService
        .getTeamBySlug(partnerSlug, { logErrors: false })
        .then((team) => {
          if (active && team) setPartner(team);
        })
        .catch(() => {});
    }
    return () => { active = false; };
  }, [isWhiteLabel, partnerSlug]);

  const edgeBranding = getCurrentBranding();
  const currentPartner = partner?.slug === partnerSlug ? partner : null;
  const fallbackPartner = edgeBranding.isPartner && edgeBranding.partnerSlug === partnerSlug
    ? { name: edgeBranding.name, branding_settings: {
        logo_url: edgeBranding.logoFull,
        support_email: edgeBranding.email,
        contact_phone: edgeBranding.phone,
      } }
    : null;
  if (isWhiteLabel && (currentPartner || fallbackPartner))
    return <PartnerFooter partner={currentPartner || fallbackPartner} />;
  if (isWhiteLabel && !partner)
    return (
      <footer className="bg-white dark:bg-gray-950 border-t border-gray-100 dark:border-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 py-6 text-center text-[13px] text-gray-400 dark:text-gray-600">
          Loading...
        </div>
      </footer>
    );

  // ProCert root domain footer is handled by ProCertFooter component in Routes.jsx
  if (isProCertDomain) return null;

  return (
    <footer className="bg-white dark:bg-gray-950 border-t border-gray-100 dark:border-gray-800/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 mb-12">
          {/* Brand */}
          <div>
            <div className="flex items-center mb-4">
              <img
                src={branding.logoFull || "/assets/branding/isogyaan-full.png"}
                alt={branding.name}
                className="h-10 w-auto object-contain"
                loading="lazy"
                decoding="async"
              />
            </div>
            <p className="text-[13px] text-gray-500 dark:text-gray-500 leading-relaxed mb-5">
              Transform compliance complexity into career confidence with
              globally recognized ISO certifications.
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                <span className="text-[12px] text-gray-500 dark:text-gray-400">
                  Exemplar Global Accredited
                </span>
              </div>
            </div>
          </div>

          {/* Platform */}
          <div className="lg:pl-4">
            <h4 className={SECTION_HEADING}>Platform</h4>
            <ul className="space-y-2.5">
              {QUICK_LINKS.map((link) => (
                <FooterLink key={link.label} href={link.href} label={link.label} />
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className={SECTION_HEADING}>Resources</h4>
            <ul className="space-y-2.5">
              {RESOURCES_LINKS.map((link) => (
                <FooterLink key={link.label} href={link.href} label={link.label} />
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className={SECTION_HEADING}>Contact</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-2.5">
                <Mail className="w-3.5 h-3.5 text-gray-400 dark:text-gray-600 mt-0.5" />
                <div>
                  <p className="text-[13px] text-gray-700 dark:text-gray-300">
                    {branding.email}
                  </p>
                  <p className="text-[11px] text-gray-400 dark:text-gray-600">
                    General Inquiries
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <Phone className="w-3.5 h-3.5 text-gray-400 dark:text-gray-600 mt-0.5" />
                <div>
                  <p className="text-[13px] text-gray-700 dark:text-gray-300">
                    {branding.phone}
                  </p>
                  <p className="text-[11px] text-gray-400 dark:text-gray-600">
                    Customer Support
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <MapPin className="w-3.5 h-3.5 text-gray-400 dark:text-gray-600 mt-0.5" />
                <div>
                  <p className="text-[13px] text-gray-700 dark:text-gray-300">
                    {branding.address?.city}
                  </p>
                  <p className="text-[11px] text-gray-400 dark:text-gray-600">
                    {branding.address?.street}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-gray-100 dark:border-gray-800/50 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[12px] text-gray-400 dark:text-gray-600">
            © {new Date().getFullYear()}{" "}
            {hostname.endsWith("isogyaan.com") ? "ISOGyaan" : "SkillCompliance"}{" "}
            Inc. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center items-center gap-5">
            <GoogleTranslate />
            {LEGAL_LINKS.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="text-[12px] text-gray-400 dark:text-gray-600 hover:text-gray-900 dark:hover:text-gray-400 transition-colors"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
